import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, Loader2, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CoverFrameSource } from '@/lib/cover/buildPreviewStillInputProps';
import type { FrameCandidates } from '@/lib/cover/resolveFrameSourceSelection';

const SOURCES: CoverFrameSource[] = ['preview', 'video'];

export type FrameSourcePickerProps = {
  candidates: FrameCandidates;
  selected: CoverFrameSource | null;
  onSelect: (source: CoverFrameSource) => void;
  /** Re-runs a failed candidate; shown as "Tentar novamente" on its card. */
  onRetry?: (source: CoverFrameSource) => void;
  /** width/height of the export, for the thumbnail boxes. */
  exportAspect: number;
  disabled?: boolean;
  /** Smaller thumbnails, for use inside another form (AI reference). */
  compact?: boolean;
};

/** The two cover-frame candidates side by side: the frame as the preview
 *  shows it, and the raw source-video frame. Shared by the "Usar frame
 *  atual" chooser and the AI cover reference. */
export function FrameSourcePicker({
  candidates,
  selected,
  onSelect,
  onRetry,
  exportAspect,
  disabled = false,
  compact = false,
}: FrameSourcePickerProps) {
  const { t } = useTranslation();
  return (
    <div
      role="radiogroup"
      aria-label={t('editor.coverImage.frameSource.label')}
      className="grid grid-cols-2 gap-3"
      data-testid="frame-source-picker"
    >
      {SOURCES.map((source) => {
        const candidate = candidates[source];
        const isSelected = selected === source;
        const unusable = candidate.status === 'error';
        return (
          <div key={source} className="relative">
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled || unusable}
              data-testid={`frame-source-${source}`}
              onClick={() => onSelect(source)}
              className={cn(
                'group flex h-full w-full flex-col items-stretch gap-2 rounded-lg border-2 p-2 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/50',
                (disabled || unusable) && 'cursor-not-allowed opacity-60',
              )}
            >
              <div
                className={cn(
                  'relative mx-auto w-full overflow-hidden rounded-md bg-black',
                  compact ? 'max-w-[96px]' : 'max-w-[180px]',
                )}
                style={{ aspectRatio: String(exportAspect) }}
              >
                {candidate.status === 'ready' && (
                  <img
                    src={candidate.dataUrl}
                    alt={t(`editor.coverImage.frameSource.${source}`)}
                    className="h-full w-full object-cover"
                  />
                )}
                {candidate.status === 'loading' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-white/80" aria-hidden="true" />
                  </div>
                )}
                {candidate.status === 'error' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-2 text-center text-[11px] text-white/70">
                    <AlertCircle className="h-4 w-4" aria-hidden="true" />
                    {t('editor.coverImage.frameSource.unavailable')}
                  </div>
                )}
                {isSelected && (
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                )}
              </div>
              <div>
                <p className="text-sm font-semibold">{t(`editor.coverImage.frameSource.${source}`)}</p>
                {!compact && (
                  <p className="text-xs text-muted-foreground">
                    {t(`editor.coverImage.frameSource.${source}Hint`)}
                  </p>
                )}
              </div>
            </button>
            {unusable && onRetry && !disabled && (
              // A sibling of the radio (never nested inside it), overlaid on the
              // error state so it reads as part of the card.
              <Button
                type="button"
                size="sm"
                variant="secondary"
                data-testid={`frame-source-retry-${source}`}
                className="absolute left-1/2 top-[58%] h-7 -translate-x-1/2 px-2.5 text-xs"
                onClick={() => onRetry(source)}
              >
                <RotateCw className="mr-1 h-3 w-3" aria-hidden="true" />
                {t('editor.coverImage.frameSource.retry')}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
